import { useQuery } from '@tanstack/react-query';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { isAxiosError } from 'helpers/clientHelpers';
import { default as Candidate } from 'model/Candidate/Candidate';
import { CANDIDATE_STATUS, CandidateStatus } from 'services/candidates';

import { QueryKey } from 'types/query';

type UseCandidateCampaignQueryProps = {
  campaignId?: number | null;
  esPersonId?: string | null;
};

export const useCandidateCampaignQuery = (
  { campaignId, esPersonId }: UseCandidateCampaignQueryProps,
  { enabled }: { enabled: boolean },
) => {
  return useQuery(
    [QueryKey.useCandidateCampaignQuery, { campaignId, esPersonId }],
    async () => {
      if (!campaignId || !esPersonId) throw new Error('Campaign ID and ES Person ID are required');

      let candidateStatus: CandidateStatus = CANDIDATE_STATUS.NEW;

      try {
        const { data: campaignCandidate } = await campaignsClient.getCampaignCandidate({ campaignId, esPersonId });

        candidateStatus = campaignCandidate.status;

        if (campaignCandidate.status === CANDIDATE_STATUS.INVISIBLE) {
          return null;
        }

        if (campaignCandidate.is_unlocked) {
          return new Candidate(campaignId, campaignCandidate);
        }
      } catch (e) {
        if (!isAxiosError(e) || e.response?.status !== 404) {
          throw e;
        }
      }

      const { data: unlockedCandidate } = await campaignsClient.createOrUpdateCampaignCandidate({
        campaignId,
        esPersonId,
        status: candidateStatus,
        is_unlocked: true,
      });

      return new Candidate(campaignId, unlockedCandidate);
    },
    {
      enabled: enabled && !!campaignId && !!esPersonId,
      staleTime: Infinity,
    },
  );
};
